import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { SessionUserBanner } from "@/components/auth/SessionUserBanner";
import {
  BookingAuthSteps,
  type AuthStep,
} from "@/components/bookingInfo/BookingAuthSteps";
import { useSessionUser } from "@/hooks/useSessionUser";
import { getUserProfile } from "@/utils/authStorage";
import {
  getDropStops,
  getPickupStops,
} from "@/services/rideBookingService";
import { stopDisplayLabel } from "@/utils/rideRouteStops";
import { useRideBooking } from "@/hooks/useRideBooking";
import type { AuthSuccessContext } from "@/viewmodels/auth/useAuthViewModel";
import { ROUTE_PATHS } from "@/routes/routePaths";
import type { RideDetails } from "@/types/rideDetails";

interface BookingAuthModalProps {
  ride: RideDetails;
  selectedSeats: number;
  totalPrice: number;
  onClose: () => void;
}

interface GuestInfo {
  name: string;
  gender: string;
}

function resolveGuestFromCookies(): GuestInfo {
  const profile = getUserProfile();
  const name = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return { name: name || "Guest", gender: profile.gender || "Male" };
}

/** Local flow phase. Once we reach "process" the booking hook's status drives the UI. */
type Phase = "auth" | "stops" | "process";

export const BookingAuthModal: React.FC<BookingAuthModalProps> = ({
  ride,
  selectedSeats,
  totalPrice,
  onClose,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const booking = useRideBooking();
  const session = useSessionUser();

  const raw = ride.raw;
  const rid = raw?.rideOffer?.rid;
  const seating = raw?.seating ?? [];
  const stops = raw?.route?.stops ?? [];
  const hasStops = stops.length > 0;

  const [phase, setPhase] = useState<Phase>(() =>
    session.isLoggedIn ? (hasStops ? "stops" : "process") : "auth"
  );
  const [authFlowKey, setAuthFlowKey] = useState(0);
  const [authStep, setAuthStep] = useState<AuthStep>("phone");

  // Stop selection (only used when the ride has intermediate stops).
  const startPoint = raw?.route?.sp;
  const endPoint = raw?.route?.ep;

  const pickupStops = useMemo(
    () => getPickupStops(stops, startPoint),
    [stops, startPoint]
  );

  const [pickupStopId, setPickupStopId] = useState<number | null>(null);
  const [dropStopId, setDropStopId] = useState<number | null>(null);

  const dropStops = useMemo(
    () => getDropStops(stops, pickupStopId ?? undefined, endPoint),
    [stops, pickupStopId, endPoint]
  );

  const singlePickup = pickupStops.length === 1;

  useEffect(() => {
    if (pickupStops.length === 1) {
      const onlyStopId = pickupStops[0].stopid;
      if (onlyStopId != null) {
        setPickupStopId(onlyStopId);
      }
      return;
    }

    setPickupStopId((current) =>
      current != null && pickupStops.some((stop) => stop.stopid === current)
        ? current
        : null
    );
  }, [pickupStops]);

  useEffect(() => {
    if (pickupStopId == null) {
      setDropStopId(null);
      return;
    }

    if (dropStops.length === 1) {
      const onlyStopId = dropStops[0].stopid;
      if (onlyStopId != null) {
        setDropStopId(onlyStopId);
      }
      return;
    }

    setDropStopId((current) =>
      current != null && dropStops.some((stop) => stop.stopid === current)
        ? current
        : null
    );
  }, [pickupStopId, dropStops]);

  // Resolved guest details + chosen stops, kept so the booking can be retried on failure.
  const [guest, setGuest] = useState<GuestInfo | null>(() =>
    session.isLoggedIn ? resolveGuestFromCookies() : null
  );
  const sessionBootstrapped = useRef(false);

  const resolveGuestInfo = (_context: AuthSuccessContext): GuestInfo =>
    resolveGuestFromCookies();

  const handleNotMe = useCallback(() => {
    session.signOutSession();
    setGuest(null);
    sessionBootstrapped.current = false;
    setAuthStep("phone");
    setAuthFlowKey((key) => key + 1);
    setPhase("auth");
  }, [session]);

  const beginBooking = (
    guestInfo: GuestInfo,
    pickup: number,
    drop: number
  ) => {
    if (rid == null) {
      toast({
        title: "Ride unavailable",
        description: "This ride can no longer be booked.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    setPhase("process");
    void booking.runBooking({
      rid,
      seating,
      seatCount: selectedSeats,
      guestName: guestInfo.name,
      gender: guestInfo.gender,
      pickupStopId: pickup,
      dropStopId: drop,
      totalPrice,
    });
  };

  const proceedAfterAuth = (guestInfo: GuestInfo) => {
    setGuest(guestInfo);
    session.refresh();

    if (hasStops) {
      setPhase("stops");
      return;
    }

    const pickup = raw?.route?.sp?.stopid;
    const drop = raw?.route?.ep?.stopid;
    if (pickup == null || drop == null) {
      toast({
        title: "Route unavailable",
        description: "Pickup and drop points are missing for this ride.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    beginBooking(guestInfo, pickup, drop);
  };

  // Skip phone/OTP when a valid session already exists in cookies.
  useEffect(() => {
    if (!session.isLoggedIn || sessionBootstrapped.current) return;
    sessionBootstrapped.current = true;
    proceedAfterAuth(resolveGuestFromCookies());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for session resume
  }, [session.isLoggedIn]);

  const handleAuthenticated = (context: AuthSuccessContext) => {
    proceedAfterAuth(resolveGuestInfo(context));
  };

  const handleStopsConfirm = () => {
    if (pickupStopId == null || dropStopId == null) {
      toast({
        title: "Select your stops",
        description: "Please choose both a pickup and a drop-off point.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    if (guest) beginBooking(guest, pickupStopId, dropStopId);
  };

  const handleRetry = () => {
    if (guest && pickupStopId != null && dropStopId != null) {
      beginBooking(guest, pickupStopId, dropStopId);
    } else if (guest && !hasStops) {
      const pickup = raw?.route?.sp?.stopid;
      const drop = raw?.route?.ep?.stopid;
      if (pickup != null && drop != null) beginBooking(guest, pickup, drop);
    }
  };

  // Close is hidden while a booking request is in flight and once it succeeds.
  const isBusy = phase === "process" && booking.status === "booking";
  const isBooked = phase === "process" && booking.status === "success";
  const showClose = !isBusy && !isBooked;

  const headerTitle = (() => {
    if (phase === "process") {
      if (booking.status === "success") return "You're all set";
      if (booking.status === "error") return "Booking failed";
      return "Almost there";
    }
    if (phase === "stops") return "Choose your stops";
    if (authStep === "phone") return "Confirm your booking";
    if (authStep === "otp") return "Verify your number";
    return "Complete your profile";
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-3xl bg-white shadow-2xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-900">{headerTitle}</h2>
          {showClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {phase === "process" ? (
            <>
              {booking.status === "success" ? (
                <BookingSuccessView
                  seats={selectedSeats}
                  bookingNumber={booking.result?.bookingNumber}
                  onDone={() => {
                    const id =
                      booking.result?.bookingNumber ||
                      (booking.result?.bookingId != null
                        ? String(booking.result.bookingId)
                        : "");
                    navigate(
                      id
                        ? `${ROUTE_PATHS.BOOKING_INFO}?bookingId=${encodeURIComponent(id)}`
                        : ROUTE_PATHS.BOOKING_INFO
                    );
                  }}
                />
              ) : booking.status === "error" ? (
                <BookingErrorView
                  message={booking.error}
                  onRetry={handleRetry}
                />
              ) : (
                <BookingConfirmingView
                  rideId={ride.rideId}
                  seats={selectedSeats}
                />
              )}
            </>
          ) : phase === "stops" ? (
            <div className="space-y-5">
              <SessionUserBanner
                variant="modal"
                displayName={session.displayName}
                isLoggedIn={session.isLoggedIn}
                showNotMe={session.showNotMe}
                onNotMe={handleNotMe}
              />

              <p className="text-sm text-gray-600">
                Where would you like to be picked up and dropped off?
              </p>

              <div className="space-y-1">
                <Label>Pickup location</Label>
                {singlePickup ? (
                  <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-900">
                    {stopDisplayLabel(pickupStops[0])}
                  </p>
                ) : (
                  <Select
                    value={pickupStopId != null ? String(pickupStopId) : ""}
                    onValueChange={(value) => {
                      const id = Number(value);
                      setPickupStopId(id);
                      if (dropStopId === id) setDropStopId(null);
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue placeholder="Choose pickup point" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      className="max-h-72 w-[var(--radix-select-trigger-width)]"
                    >
                      {pickupStops.map((s) => (
                        <SelectItem
                          key={s.stopid}
                          value={String(s.stopid)}
                          className="whitespace-normal break-words pr-2"
                        >
                          {stopDisplayLabel(s)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-1">
                <Label>Select drop-off location</Label>
                <Select
                  value={dropStopId != null ? String(dropStopId) : ""}
                  onValueChange={(value) => setDropStopId(Number(value))}
                  disabled={pickupStopId == null}
                >
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue
                      placeholder={
                        pickupStopId == null
                          ? "Select pickup first"
                          : "Choose drop-off point"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    className="max-h-72 w-[var(--radix-select-trigger-width)]"
                  >
                    {dropStops.map((s) => (
                      <SelectItem
                        key={s.stopid}
                        value={String(s.stopid)}
                        className="whitespace-normal break-words pr-2"
                      >
                        {stopDisplayLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleStopsConfirm}
                disabled={pickupStopId == null || dropStopId == null}
                className="h-12 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700"
              >
                Confirm Booking
              </Button>
            </div>
          ) : (
            <BookingAuthSteps
              key={authFlowKey}
              ride={ride}
              selectedSeats={selectedSeats}
              onAuthenticated={handleAuthenticated}
              onStepChange={setAuthStep}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/** Holding screen shown while the booking request is in flight. */
const BookingConfirmingView: React.FC<{ rideId: string; seats: number }> = ({
  rideId,
  seats,
}) => (
  <div className="flex flex-col items-center py-8 text-center">
    <div className="relative mb-8 flex h-28 w-28 items-center justify-center">
      <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/30" />
      <span className="absolute inset-2 rounded-full bg-emerald-100" />
      <Loader2 className="relative h-12 w-12 animate-spin text-emerald-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">Confirming your booking</h3>
    <p className="mt-2 max-w-xs text-sm text-gray-500">
      Hang tight — we&apos;re securing {seats} {seats === 1 ? "seat" : "seats"} on
      ride <span className="font-semibold text-gray-700">{rideId}</span> for you.
    </p>
    <div className="mt-6 flex items-center gap-1.5">
      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-500 [animation-delay:-0.3s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-500 [animation-delay:-0.15s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-500" />
    </div>
  </div>
);

/** Success screen with a celebratory animation. */
const BookingSuccessView: React.FC<{
  seats: number;
  bookingNumber?: string;
  onDone: () => void;
}> = ({ seats, bookingNumber, onDone }) => (
  <div className="flex flex-col items-center py-8 text-center">
    <div className="relative mb-8 flex h-28 w-28 items-center justify-center">
      <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/30" />
      <span className="absolute inset-2 rounded-full bg-emerald-100" />
      <CheckCircle2 className="animate-in zoom-in relative h-16 w-16 text-emerald-600 duration-500" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">
      {seats} {seats === 1 ? "seat" : "seats"} booked successfully!
    </h3>
    <p className="mt-2 max-w-xs text-sm text-gray-500">
      Your ride is confirmed. Pay cash to the host when you board.
    </p>
    {bookingNumber && (
      <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
        Booking #{bookingNumber}
      </p>
    )}
    <Button
      onClick={onDone}
      className="mt-8 h-12 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700"
    >
      View Booking
    </Button>
  </div>
);

/** Error screen with a retry action. */
const BookingErrorView: React.FC<{
  message: string | null;
  onRetry: () => void;
}> = ({ message, onRetry }) => (
  <div className="flex flex-col items-center py-8 text-center">
    <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
      <AlertCircle className="h-10 w-10 text-red-500" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">We couldn&apos;t book it</h3>
    <p className="mt-2 max-w-xs text-sm text-gray-500">
      {message || "Something went wrong. Please try again."}
    </p>
    <Button
      onClick={onRetry}
      className="mt-8 h-12 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700"
    >
      Try Again
    </Button>
  </div>
);
